import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage, LANGUAGES } from "../contexts/LanguageContext";
import "./css/Navbar.css";

export default function Navbar({ theme = "light" }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  // helper to navigate to a pathname + hash, then scroll the element into view
  const goTo = (pathname = "/", hash = "") => {
    setIsOpen(false);

    // If already on the target pathname, just set the hash and scroll
    if (location.pathname === pathname) {
      if (hash) {
        // set hash without reloading and scroll into view
        window.history.replaceState(null, "", `${pathname}#${hash}`);
        requestAnimationFrame(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } else {
        // navigate to pathname (might be same) and scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    // navigate to the pathname first, then set the hash and scroll after navigation
    navigate(pathname, { replace: false });

    // wait a frame to allow the route to render, then set hash and scroll
    setTimeout(() => {
      if (hash) {
        window.history.replaceState(null, "", `${pathname}#${hash}`);
        requestAnimationFrame(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 50);
  };

  return (
    <nav className={`navbar ${theme}`}>
      {/* Burger button */}
      <div
        className={`burger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </div>

      {/* Links */}
      <div className={`nav-links ${isOpen ? "open" : ""}`}>
        <a href="#about" onClick={(e) => { e.preventDefault(); goTo("/", "about"); }} role="button" tabIndex={0}>
          {t("navbar.about")}
        </a>

        <a href="/paintings" onClick={(e) => { e.preventDefault(); goTo("/paintings", ""); }} role="button" tabIndex={0}>
          {t("navbar.paintings")}
        </a>

        <a href="/exhibitions" onClick={(e) => { e.preventDefault(); goTo("/exhibitions", ""); }} role="button" tabIndex={0}>
          {t("navbar.exhibitions")}
        </a>

        {/* lucacel: should go to landing page section #lucacel */}
        <a href="#lucacel" onClick={(e) => { e.preventDefault(); goTo("/", "lucacel"); }} role="button" tabIndex={0}>
          {t("navbar.lucacel")}
        </a>

        <a href="#contact" onClick={(e) => { e.preventDefault(); goTo("/", "contact"); }} role="button" tabIndex={0}>
          {t("navbar.contact")}
        </a>

        {/* Language selector */}
        <div className="language-switch" role="group" aria-label={t("language.label")}>
          {LANGUAGES.map((code, i) => (
            <React.Fragment key={code}>
              {i > 0 && <span className="language-separator" aria-hidden="true">/</span>}
              <button
                type="button"
                lang={code}
                className={`language-option ${language === code ? "active" : ""}`}
                aria-pressed={language === code}
                onClick={() => setLanguage(code)}
              >
                {code.toUpperCase()}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
    </nav>
  );
}
